import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Currency } from '@freelancer/datastore/collections';
import { trackByValue } from '@freelancer/ui';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { IconSize } from '@freelancer/ui/icon';
import {
  FontColor,
  FontWeight,
  TextAlign,
  TextTransform,
} from '@freelancer/ui/text';
import type {
  BidUpgradeType,
  EntryUpgradeType,
  UpgradeType,
} from '@freelancer/ui/upgrade-tag';

@Component({
  selector: 'fl-upgrade-item',
  template: `
    <fl-grid
      [vAlign]="
        variation === 'compact' ? VerticalAlignment.VERTICAL_CENTER : undefined
      "
    >
      <ng-container *ngIf="variation === 'default'">
        <fl-col
          [col]="6"
          [colTablet]="3"
          [flMarginBottom]="'xxsmall'"
        >
          <ng-container [ngTemplateOutlet]="upgradeTag"></ng-container>
        </fl-col>
        <fl-col
          [col]="6"
          [colTablet]="3"
          [colTabletOrder]="3"
          [flMarginBottom]="'xxsmall'"
        >
          <ng-container [ngTemplateOutlet]="upgradePrice"></ng-container>
        </fl-col>
        <fl-col
          [col]="12"
          [colTablet]="6"
        >
          <ng-container [ngTemplateOutlet]="description"></ng-container>
        </fl-col>
      </ng-container>

      <ng-container *ngIf="variation === 'compact'">
        <fl-col
          [col]="12"
          [colTablet]="9"
        >
          <div
            class="UpgradeCompactHeader"
            [flMarginBottom]="'xxsmall'"
          >
            <ng-container [ngTemplateOutlet]="upgradeTag"></ng-container>
            <div [flShowMobile]="true">
              <ng-container [ngTemplateOutlet]="upgradePrice"></ng-container>
            </div>
          </div>

          <ng-container [ngTemplateOutlet]="description"></ng-container>
        </fl-col>
        <fl-col
          [col]="3"
          [flHideMobile]="true"
        >
          <ng-container [ngTemplateOutlet]="upgradePrice"></ng-container>
        </fl-col>
      </ng-container>
    </fl-grid>

    <ng-template #upgradeTag>
      <div class="UpgradeItem">
        <fl-upgrade-tag
          class="UpgradeItem-tag"
          [attr.data-variation]="variation"
          [upgradeType]="upgradeType"
        ></fl-upgrade-tag>
      </div>
    </ng-template>

    <ng-template #description>
      <p [flMarginBottomDesktop]="'none'">
        <ng-content></ng-content>
      </p>
    </ng-template>

    <ng-template #upgradePrice>
      <ng-container *ngIf="!isLoadingPrice; else loadingPrice">
        <div class="UpgradePricingWrapper">
          <div
            class="UpgradePrice"
            [flMarginRight]="'xxsmall'"
          >
            <ng-container *ngIf="!isSoldOut">
              <p
                class="text-right font-bold"
                *ngIf="!upgradeApplied && !promoPrice && price && !freeUpgrade"
              >
                {{ price | flCurrency: currency?.code }}
              </p>
              <p
                class="text-right uppercase font-bold"
                *ngIf="
                  !upgradeApplied &&
                  ((!promoPrice && price === 0) || freeUpgrade)
                "
                i18n="Free tag for an upgrade item"
              >
                Free
              </p>
              <p
                class="text-right font-bold"
                *ngIf="upgradeApplied && !promoPrice"
                i18n="Project upgrade applied tag"
              >
                Upgrade Applied
              </p>
              <ng-container
                *ngIf="!upgradeApplied && promoPrice && price && !freeUpgrade"
              >
                <p
                  class="text-color-inherit PromoText text-right font-bold"
                  i18n="Project upgrade promo price"
                >
                  ONLY
                  {{ promoPrice | flCurrency: currency.code }}
                </p>
                <p
                  class="UpgradeItem-price-full text-right font-bold"
                  i18n="Project upgrade promo price"
                >
                  {{ price | flCurrency: currency?.code }}
                </p>
              </ng-container>
            </ng-container>
            <p
              class="text-right font-bold"
              *ngIf="isSoldOut"
              i18n="Project upgrade sold out"
            >
              SOLD OUT
            </p>
          </div>
          <fl-icon
            *ngIf="tooltip"
            [matTooltip]="tooltip"
            [matTooltipPosition]="'above'"
            [name]="'ui-info-outline'"
            [size]="IconSize.SMALL"
          >
          </fl-icon>
        </div>
      </ng-container>
      <ng-template #loadingPrice>
        <fl-loading-text
          [rows]="1"
          [padded]="false"
        ></fl-loading-text>
      </ng-template>
    </ng-template>
  `,
  styleUrls: ['./upgrade-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpgradeItemComponent {
  FontWeight = FontWeight;
  FontColor = FontColor;
  IconSize = IconSize;
  TextAlign = TextAlign;
  TextTransform = TextTransform;
  VerticalAlignment = VerticalAlignment;
  trackByValue = trackByValue;

  @Input() currency: Currency;
  @Input() freeUpgrade: boolean;
  @Input() isLoadingPrice: boolean;
  @Input() isSoldOut: boolean;
  @Input() price: number;
  @Input() promoPrice: number;
  @Input() upgradeApplied: boolean;
  @Input() upgradeType: UpgradeType | BidUpgradeType | EntryUpgradeType;
  @Input() tooltip: string;

  @Input()
  variation: 'default' | 'compact' = 'default';
}
